import { useState, useRef, useEffect } from 'react';
import {
  Plus, Delete, Search, Save, FileText, User, Package, CreditCard,
  DollarSign, X, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Settings, Shield,
  Loader2, Calendar, Repeat, CheckCircle, MessageSquare
} from 'lucide-react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { recurringApi, receptoresApi, productosApi, settingsApi, parametrosApi, documentsApi } from '../utils/api';
import useAuthStore from '../store/authStore';
import useEscClose from '../hooks/useEscClose';
import InvoiceTemplate from './InvoiceTemplate';
import FormularioCliente from '../pages/clientes/FormularioCliente';
import ExoneracionModal from './ExoneracionModal';

const CONDICIONES_VENTA = [
  { value: '01', label: 'Contado' },
  { value: '02', label: 'Crédito' },
];

const MEDIOS_PAGO = [
  { value: '01', label: 'Efectivo', icon: DollarSign },
  { value: '02', label: 'Tarjeta', icon: CreditCard },
  { value: '04', label: 'Transferencia', icon: FileText },
];

const FRECUENCIA_LABELS = {
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  yearly: 'Anual'
};

const formatCurrencyDisplay = (amount, moneda = 'CRC') => {
  const num = Number(amount) || 0;
  return num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function CrearPlantillaRecurrenteModal({ onClose, onSave, plantilla = null, documento = null }) {
  useEscClose(onClose);
  const { currentEmisor, tipoCambio: storeTipoCambio } = useAuthStore();

  // Mode: create plantilla, edit plantilla, or edit documento
  const isEditMode = !!plantilla || !!documento;
  const isDocumentoMode = !!documento; // Editing a generated document (no recurrence config)

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(isEditMode);
  const [previewMode, setPreviewMode] = useState('split'); // 'split' | 'hidden' | 'full'
  const [showRecurrenceConfig, setShowRecurrenceConfig] = useState(false);

  // Client states
  const [receptor, setReceptor] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [showOpcionesModal, setShowOpcionesModal] = useState(false);
  const [showNuevoClienteModal, setShowNuevoClienteModal] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);
  const [actividadesCliente, setActividadesCliente] = useState([]);
  const [cargandoActividades, setCargandoActividades] = useState(false);

  // Product states
  const productInputRef = useRef(null);
  const productListRef = useRef(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Document states
  const [documentoState, setDocumentoState] = useState({
    condicionVenta: '01',
    medioPago: '01',
    moneda: 'USD',
    tipoCambio: storeTipoCambio || 1,
    comentarios: '',
    diasCredito: 30,
    referenciaOC: ''
  });
  const [lineas, setLineas] = useState([]);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [draggedLineId, setDraggedLineId] = useState(null);

  // Recurrence config
  const [recurrenceConfig, setRecurrenceConfig] = useState({
    nombre: '',
    frecuencia: 'monthly',
    diasGeneracion: [12, 28],
    proximaGeneracion: dayjs().add(1, 'day').format('YYYY-MM-DD'),
    periodoCobroEnabled: false,
    periodoCobroLabel: 'Periodo de Cobro',
    periodoCobroOffset: 0 // 0=mes actual, -1=mes anterior, -2=2 meses atrás
  });

  // Emisor data
  const [emisorData, setEmisorData] = useState(null);

  // Tarifas IVA
  const [tarifasIva, setTarifasIva] = useState([]);
  const tarifaDefault = tarifasIva.find(t => t.codigo === '08') || { codigo: '08', porcentaje: 13 };

  // Exoneración states
  const [exoneracionTemporal, setExoneracionTemporal] = useState(null);
  const [showExoneracionRapidaModal, setShowExoneracionRapidaModal] = useState(false);

  // Load emisor data
  useEffect(() => {
    const loadEmisorData = async () => {
      try {
        const response = await settingsApi.getEmisor();
        if (response.data?.data) {
          setEmisorData(response.data.data);
        }
      } catch (error) {
        console.error('Error loading emisor:', error);
      }
    };
    loadEmisorData();
  }, [currentEmisor]);

  // Load tarifas IVA
  useEffect(() => {
    const cargarTarifasIva = async () => {
      try {
        const response = await parametrosApi.listTarifasIva();
        setTarifasIva(response.data?.data || []);
      } catch (error) {
        console.error('Error cargando tarifas IVA:', error);
      }
    };
    cargarTarifasIva();
  }, []);

  // Load template data when editing
  useEffect(() => {
    const cargarPlantilla = async () => {
      if (!plantilla?.id) {
        setLoadingTemplate(false);
        return;
      }

      setLoadingTemplate(true);
      try {
        // Get full template data including lines
        const response = await recurringApi.getById(plantilla.id);
        const data = response.data?.data || response.data;

        if (data) {
          // Load receptor
          let loadedReceptor = null;
          if (data.receptorId) {
            try {
              const receptorRes = await receptoresApi.getById(data.receptorId);
              const r = receptorRes.data?.data;
              loadedReceptor = r;
              if (r) {
                setReceptor({
                  id: r.id,
                  nombre: r.nombre,
                  identificacion: r.identificacion,
                  tipoIdentificacion: r.tipoIdentificacion || r.tipo_identificacion,
                  email: r.email,
                  emailFacturacion: r.emailFacturacion || r.email_facturacion,
                  telefonoCodigo: r.telefonoCodigo || r.telefono_codigo,
                  telefonoNumero: r.telefonoNumero || r.telefono_numero,
                  provincia: r.provincia,
                  canton: r.canton,
                  distrito: r.distrito,
                  otrasSenas: r.otrasSenas || r.otras_senas,
                  diasCredito: r.diasCredito || r.dias_credito,
                  exoneraciones: r.exoneraciones || [],
                  exoneracionActiva: r.exoneracionActiva || r.exoneracion_activa,
                  exoneracionNumero: r.exoneracionNumero || r.exoneracion_numero,
                  exoneracionTipoDoc: r.exoneracionTipoDoc || r.exoneracion_tipo_doc,
                  exoneracionPorcentaje: r.exoneracionPorcentaje || r.exoneracion_porcentaje
                });
              }
            } catch (e) {
              console.error('Error loading receptor:', e);
              setReceptor({
                id: data.receptorId,
                nombre: data.receptorNombre || 'Cliente',
                identificacion: data.receptorIdentificacion
              });
            }
          }

          // Load document settings
          setDocumentoState({
            condicionVenta: data.condicionVenta || '01',
            medioPago: Array.isArray(data.medioPago) ? data.medioPago[0] : (data.medioPago || '01'),
            moneda: data.codigoMoneda || 'USD',
            tipoCambio: storeTipoCambio || 1,
            comentarios: data.notas || '',
            diasCredito: 30,
            referenciaOC: data.referenciaOC || data.referencia_oc || ''
          });

          // Load lines
          const lineasCargadas = (data.lineas || []).map((l, idx) => ({
            id: Date.now() + idx,
            cantidad: l.cantidad || 1,
            detalle: l.detalle,
            codigo: l.codigo || '',
            sku: l.sku || l.codigoComercial || '',
            precioUnitario: l.precioUnitario || 0,
            iva: l.impuestoTarifa || 13,
            codigoTarifa: l.impuestoCodigoTarifa || '08',
            impuestosAdicionales: l.impuestosAdicionales || [],
            impuestoExoneracion: l.impuestoExoneracion || null,
            descuento: l.descuentoPorcentaje || 0,
            unidadMedida: l.unidadMedida || 'Unid'
          }));
          setLineas(lineasCargadas);

          // Use receptor's current active exoneracion (not the one saved in template)
          // loadedReceptor.exoneraciones already contains only active + non-expired from API
          const receptorExos = loadedReceptor?.exoneraciones || [];
          const activeExo = receptorExos[0];
          if (activeExo) {
            const exoData = {
              tipoDocumento: activeExo.tipoDocumento,
              numeroDocumento: activeExo.numeroDocumento,
              nombreInstitucion: activeExo.institucionNombre,
              fechaEmision: activeExo.fechaEmision,
              fechaVencimiento: activeExo.fechaVencimiento,
              porcentaje: activeExo.porcentaje,
              cabys: activeExo.cabys || null,
            };
            setExoneracionTemporal(exoData);
            setLineas(prev => prev.map(l => ({ ...l, impuestoExoneracion: exoData })));
          } else {
            // No active exoneracion on receptor - clear old ones from template lines
            setExoneracionTemporal(null);
            setLineas(prev => prev.map(l => ({ ...l, impuestoExoneracion: null })));
          }

          // Load recurrence config
          setRecurrenceConfig({
            nombre: data.nombre || '',
            frecuencia: data.frecuencia || 'monthly',
            diasGeneracion: data.diasGeneracion || [12, 28],
            proximaGeneracion: data.proximaGeneracion
              ? dayjs(data.proximaGeneracion).format('YYYY-MM-DD')
              : dayjs().add(1, 'day').format('YYYY-MM-DD'),
            periodoCobroEnabled: !!data.periodoCobroEnabled,
            periodoCobroLabel: data.periodoCobroLabel || 'Periodo de Cobro',
            periodoCobroOffset: data.periodoCobroOffset || 0
          });
        }
      } catch (error) {
        console.error('Error cargando plantilla:', error);
        toast.error('Error al cargar plantilla');
      } finally {
        setLoadingTemplate(false);
      }
    };

    cargarPlantilla();
  }, [plantilla?.id, storeTipoCambio]);

  // Load document data when editing a generated document
  useEffect(() => {
    const cargarDocumento = async () => {
      if (!documento?.id) {
        return;
      }

      setLoadingTemplate(true);
      try {
        const response = await documentsApi.getById(documento.id);
        const data = response.data?.data || response.data;

        if (data) {
          // Load receptor - try to get full data from receptores API
          const receptorId = data.receptorId || data.receptor_id || data.receptor?.id;
          if (receptorId) {
            try {
              const receptorRes = await receptoresApi.getById(receptorId);
              const r = receptorRes.data?.data;
              if (r) {
                setReceptor({
                  id: r.id,
                  nombre: r.nombre,
                  identificacion: r.identificacion,
                  tipoIdentificacion: r.tipoIdentificacion || r.tipo_identificacion,
                  email: r.email,
                  emailFacturacion: r.emailFacturacion || r.email_facturacion,
                  telefonoCodigo: r.telefonoCodigo || r.telefono_codigo,
                  telefonoNumero: r.telefonoNumero || r.telefono_numero,
                  provincia: r.provincia,
                  canton: r.canton,
                  distrito: r.distrito,
                  otrasSenas: r.otrasSenas || r.otras_senas,
                  diasCredito: r.diasCredito || r.dias_credito,
                  exoneraciones: r.exoneraciones || [],
                  exoneracionActiva: r.exoneracionActiva || r.exoneracion_activa,
                  exoneracionNumero: r.exoneracionNumero || r.exoneracion_numero,
                  exoneracionTipoDoc: r.exoneracionTipoDoc || r.exoneracion_tipo_doc,
                  exoneracionPorcentaje: r.exoneracionPorcentaje || r.exoneracion_porcentaje,
                  exoneracionCabys: r.exoneracionCabys || r.exoneracion_cabys
                });
              }
            } catch (e) {
              console.error('Error loading receptor:', e);
              // Fallback to document data
              setReceptor({
                id: receptorId,
                nombre: data.receptorNombre || data.receptor_nombre || 'Cliente',
                identificacion: data.receptorIdentificacion || data.receptor_identificacion
              });
            }
          } else if (data.receptorNombre || data.receptor_nombre) {
            setReceptor({
              nombre: data.receptorNombre || data.receptor_nombre,
              identificacion: data.receptorIdentificacion || data.receptor_identificacion
            });
          }

          // Load document settings
          setDocumentoState({
            condicionVenta: data.condicionVenta || data.condicion_venta || '01',
            medioPago: Array.isArray(data.medioPago) ? data.medioPago[0] : (data.medioPago || data.medio_pago || '01'),
            moneda: data.codigoMoneda || data.codigo_moneda || 'USD',
            tipoCambio: data.tipoCambio || data.tipo_cambio || storeTipoCambio || 1,
            comentarios: data.notas || '',
            diasCredito: data.diasCredito || data.dias_credito || 30,
            referenciaOC: data.referenciaOC || data.referencia_oc || data.metadata?.ordenCompra || ''
          });

          // Load lines
          const lineasDoc = data.lineas || [];
          const lineasCargadas = lineasDoc.map((l, idx) => ({
            id: Date.now() + idx,
            cantidad: l.cantidad || 1,
            detalle: l.detalle || l.Detalle || '',
            codigo: l.codigo || l.Codigo || '',
            sku: l.sku || l.codigoComercial || l.codigo_comercial || '',
            precioUnitario: l.precioUnitario || l.PrecioUnitario || 0,
            iva: l.impuestoTarifa || l.ImpuestoTarifa || 13,
            codigoTarifa: l.impuestoCodigoTarifa || '08',
            impuestosAdicionales: l.impuestosAdicionales || [],
            impuestoExoneracion: l.impuestoExoneracion || l.impuesto_exoneracion || null,
            descuento: l.descuentoPorcentaje || l.DescuentoPorcentaje || 0,
            unidadMedida: l.unidadMedida || l.UnidadMedida || 'Unid'
          }));
          setLineas(lineasCargadas);

          // Si hay exoneración en las líneas del documento, cargarla como exoneración temporal
          const lineaConExoDoc = lineasDoc.find(l => l.impuestoExoneracion || l.impuesto_exoneracion);
          if (lineaConExoDoc) {
            const exo = lineaConExoDoc.impuestoExoneracion || lineaConExoDoc.impuesto_exoneracion;
            setExoneracionTemporal(exo);
          }
        }
      } catch (error) {
        console.error('Error cargando documento:', error);
        toast.error('Error al cargar documento');
      } finally {
        setLoadingTemplate(false);
      }
    };

    cargarDocumento();
  }, [documento?.id, storeTipoCambio]);

  // Client search
  const buscarCliente = async (term) => {
    if (term.length < 2) {
      setClientSearchResults([]);
      return;
    }
    setSearchingClient(true);
    try {
      const response = await receptoresApi.search(term);
      setClientSearchResults(response.data?.data || []);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientSearchResults([]);
    } finally {
      setSearchingClient(false);
    }
  };

  // Debounced client search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearchTerm) {
        buscarCliente(clientSearchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearchTerm]);

  // Product search with debounce
  useEffect(() => {
    if (productSearchTerm.length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      setHighlightedIndex(-1);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const response = await productosApi.list({ q: productSearchTerm, limit: 10 });
        setProductSearchResults(response.data?.data || []);
        setShowProductDropdown(true);
        setHighlightedIndex(-1);
      } catch (error) {
        console.error('Error searching products:', error);
        setProductSearchResults([]);
      } finally {
        setSearchingProducts(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  const seleccionarCliente = async (cliente) => {
    // Load full receptor with exoneraciones
    try {
      const fullRes = await receptoresApi.getById(cliente.id);
      const full = fullRes.data?.data;
      if (full) {
        setReceptor({ ...cliente, exoneraciones: full.exoneraciones || [] });
        // Auto-apply active exoneración
        const activeExo = (full.exoneraciones || [])[0];
        if (activeExo) {
          const exoData = {
            tipoDocumento: activeExo.tipoDocumento,
            numeroDocumento: activeExo.numeroDocumento,
            nombreInstitucion: activeExo.institucionNombre,
            fechaEmision: activeExo.fechaEmision,
            fechaVencimiento: activeExo.fechaVencimiento,
            porcentaje: activeExo.porcentaje,
            cabys: activeExo.cabys || null,
          };
          setExoneracionTemporal(exoData);
          // Apply to existing lines
          setLineas(prev => prev.map(l => ({ ...l, impuestoExoneracion: exoData })));
        } else {
          setExoneracionTemporal(null);
        }
      } else {
        setReceptor(cliente);
      }
    } catch (e) {
      setReceptor(cliente);
    }
    setShowClientModal(false);
    setClientSearchTerm('');
    setClientSearchResults([]);

    // Auto-configure
    const clientMoneda = cliente.moneda_defecto || 'USD';
    setDocumentoState(prev => ({
      ...prev,
      moneda: clientMoneda,
      tipoCambio: clientMoneda === 'USD' ? (storeTipoCambio || 1) : 1,
      condicionVenta: cliente.condicion_venta_defecto || '01',
      diasCredito: cliente.dias_credito || 30
    }));

    // Auto-fill recurrence name
    setRecurrenceConfig(prev => ({
      ...prev,
      nombre: `Factura recurrente - ${cliente.nombre}`
    }));

    toast.success(`Cliente: ${cliente.nombre}`);

    // Fetch activities
    setCargandoActividades(true);
    try {
      const response = await receptoresApi.consultarHacienda(cliente.identificacion);
      if (response.data?.success && response.data?.data?.actividades) {
        setActividadesCliente(response.data.data.actividades);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setCargandoActividades(false);
    }
  };

  const seleccionarProducto = (producto) => {
    const impuestosProducto = producto.impuestos || [];
    const impuestoIva = impuestosProducto.find(i => i.codigoImpuesto === '01');
    const impuestosAdicionales = impuestosProducto.filter(i => i.codigoImpuesto !== '01');

    agregarLinea({
      detalle: producto.descripcion || producto.nombre,
      codigo: producto.codigoCabys || '',
      sku: producto.codigo || '',
      precio: producto.precio || 0,
      iva: impuestoIva?.porcentaje || producto.tarifaIva || tarifaDefault.porcentaje,
      codigoTarifa: impuestoIva?.codigo || producto.codigoTarifa || tarifaDefault.codigo,
      impuestosAdicionales: impuestosAdicionales,
      unidadMedida: producto.unidadMedida || 'Unid'
    });
    setProductSearchTerm('');
    setShowProductDropdown(false);
    setProductSearchResults([]);
    setSelectedProducts([]);
    productInputRef.current?.focus();
  };

  const agregarLinea = (producto = null) => {
    const newId = Date.now();
    const nuevaLinea = producto ? {
      id: newId,
      cantidad: 1,
      detalle: producto.nombre || producto.detalle,
      codigo: producto.codigo || '',
      sku: producto.sku || '',
      precioUnitario: producto.precio || 0,
      iva: producto.iva || tarifaDefault.porcentaje,
      codigoTarifa: producto.codigoTarifa || tarifaDefault.codigo,
      impuestosAdicionales: producto.impuestosAdicionales || [],
      descuento: 0,
      unidadMedida: producto.unidadMedida || 'Unid'
    } : {
      id: newId,
      cantidad: 1,
      detalle: '',
      codigo: '',
      sku: '',
      precioUnitario: 0,
      iva: tarifaDefault.porcentaje,
      codigoTarifa: tarifaDefault.codigo,
      impuestosAdicionales: [],
      descuento: 0,
      unidadMedida: 'Unid'
    };

    setLineas([...lineas, nuevaLinea]);
    return newId;
  };

  const actualizarLinea = (id, campo, valor) => {
    setLineas(lineas.map(l => l.id === id ? { ...l, [campo]: valor } : l));
  };

  const eliminarLinea = (id) => {
    if (id) {
      setLineas(lineas.filter(l => l.id !== id));
      setSelectedLineIds(prev => prev.filter(i => i !== id));
    } else if (selectedLineIds.length > 0) {
      setLineas(lineas.filter(l => !selectedLineIds.includes(l.id)));
      setSelectedLineIds([]);
    }
  };

  const toggleLineSelection = (id) => {
    setSelectedLineIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAllLines = () => {
    if (selectedLineIds.length === lineas.length) {
      setSelectedLineIds([]);
    } else {
      setSelectedLineIds(lineas.map(l => l.id));
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, lineaId) => {
    setDraggedLineId(lineaId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedLineId(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetLineaId) => {
    e.preventDefault();
    if (!draggedLineId || draggedLineId === targetLineaId) {
      setDraggedLineId(null);
      return;
    }

    const draggedIndex = lineas.findIndex(l => l.id === draggedLineId);
    const targetIndex = lineas.findIndex(l => l.id === targetLineaId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedLineId(null);
      return;
    }

    const newLineas = [...lineas];
    const [draggedItem] = newLineas.splice(draggedIndex, 1);
    newLineas.splice(targetIndex, 0, draggedItem);

    setLineas(newLineas);
    setDraggedLineId(null);
  };

  // Product search keyboard navigation
  const handleProductSearch = (e) => {
    const resultsCount = productSearchResults.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (resultsCount > 0) {
        setHighlightedIndex(prev => (prev < resultsCount - 1 ? prev + 1 : 0));
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (resultsCount > 0) {
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : resultsCount - 1));
      }
      return;
    }

    if (e.key === 'Escape') {
      setShowProductDropdown(false);
      setHighlightedIndex(-1);
      return;
    }

    if (e.key === 'Enter' && productSearchTerm.trim()) {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < resultsCount) {
        seleccionarProducto(productSearchResults[highlightedIndex]);
        setHighlightedIndex(-1);
        return;
      }

      const newId = agregarLinea({ detalle: productSearchTerm, codigo: '', precio: 0, iva: 13 });
      setProductSearchTerm('');
      setHighlightedIndex(-1);

      setTimeout(() => {
        const priceInput = document.querySelector(`input[data-line-price="${newId}"]`);
        if (priceInput) priceInput.focus();
      }, 100);
    }
  };

  // Quitar exoneración
  const quitarExoneracion = () => {
    setExoneracionTemporal(null);
    toast.success('Exoneración eliminada');
  };

  // Formatear fecha para mostrar
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    try {
      return new Date(fecha).toLocaleDateString('es-CR');
    } catch {
      return fecha;
    }
  };

  // Exoneración helpers
  const lineaTieneExoneracion = (linea) => {
    // Check multiple exoneraciones first
    if (receptor?.exoneraciones?.length > 0) {
      const cabysLinea = linea.codigo || '';
      for (const exo of receptor.exoneraciones) {
        const cabys = typeof exo.cabys === 'string' ? JSON.parse(exo.cabys || '[]') : (exo.cabys || []);
        if (!cabys || cabys.length === 0) return true; // No restriction
        if (cabysLinea && cabys.some(c => cabysLinea === c)) return true;
      }
      return false;
    }
    // Luego verificar exoneración temporal
    if (exoneracionTemporal) return true;
    // Finalmente verificar exoneración legacy del receptor
    const exoneracionActiva = receptor?.exoneracionActiva || receptor?.exoneracion_activa;
    if (!exoneracionActiva) return false;
    const cabysAutorizados = receptor?.exoneracionCabys || receptor?.exoneracion_cabys;
    if (!cabysAutorizados || cabysAutorizados.length === 0) return true;
    const cabysLinea = linea.codigo || '';
    if (!cabysLinea) return false;
    let cabysArray = cabysAutorizados;
    if (typeof cabysAutorizados === 'string') {
      try { cabysArray = JSON.parse(cabysAutorizados); } catch { return false; }
    }
    return cabysArray.some(cabysAuth => cabysLinea === cabysAuth);
  };

  const getPorcentajeExoneracion = (linea = null) => {
    // Primero verificar si la línea tiene exoneración guardada
    if (linea?.impuestoExoneracion) return linea.impuestoExoneracion.porcentaje || 13;
    if (exoneracionTemporal) return exoneracionTemporal.porcentaje || 13;
    return receptor?.exoneracionPorcentaje || receptor?.exoneracion_porcentaje || 0;
  };

  const getExoneracionData = (linea = null) => {
    // Primero verificar si la línea tiene exoneración guardada
    if (linea?.impuestoExoneracion) return linea.impuestoExoneracion;
    if (exoneracionTemporal) return exoneracionTemporal;
    return {
      tipoDocumento: receptor?.exoneracionTipoDoc || receptor?.exoneracion_tipo_doc || '04',
      tipoDocumentoDesc: receptor?.exoneracionTipoDocDesc || receptor?.exoneracion_tipo_doc_desc || 'Exoneración',
      numeroDocumento: receptor?.exoneracionNumero || receptor?.exoneracion_numero,
      nombreInstitucion: receptor?.exoneracionInstitucionCodigo || receptor?.exoneracion_institucion_codigo || '01',
      fechaEmision: receptor?.exoneracionFechaEmision || receptor?.exoneracion_fecha_emision,
      porcentaje: receptor?.exoneracionPorcentaje || receptor?.exoneracion_porcentaje || 13
    };
  };

  // Cálculos
  const calcularSubtotal = () => {
    return lineas.reduce((sum, l) => {
      const subtotal = l.cantidad * l.precioUnitario;
      const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
      return sum + (subtotal - descuentoMonto);
    }, 0);
  };

  const calcularIVA = () => {
    return lineas.reduce((sum, l) => {
      const subtotal = l.cantidad * l.precioUnitario;
      const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
      const base = subtotal - descuentoMonto;
      const ivaOriginal = base * (l.iva / 100);

      if (lineaTieneExoneracion(l)) {
        const porcentajeExoneracion = getPorcentajeExoneracion(l);
        const ivaExonerado = base * (Math.min(porcentajeExoneracion, l.iva) / 100);
        return sum + (ivaOriginal - ivaExonerado);
      }

      return sum + ivaOriginal;
    }, 0);
  };

  const calcularImpuestosAdicionales = () => {
    const totales = {};
    lineas.forEach(l => {
      if (!l.impuestosAdicionales || l.impuestosAdicionales.length === 0) return;
      const subtotal = l.cantidad * l.precioUnitario;
      const descuentoMonto = subtotal * ((l.descuento || 0) / 100);
      const base = subtotal - descuentoMonto;

      l.impuestosAdicionales.forEach(imp => {
        const monto = base * (imp.porcentaje / 100);
        const key = `${imp.codigoImpuesto}-${imp.codigo}`;
        if (!totales[key]) {
          totales[key] = { ...imp, monto: 0 };
        }
        totales[key].monto += monto;
      });
    });
    return Object.values(totales);
  };

  const calcularTotalImpuestosAdicionales = () => {
    return calcularImpuestosAdicionales().reduce((sum, imp) => sum + imp.monto, 0);
  };

  const calcularTotal = () => {
    return calcularSubtotal() + calcularIVA() + calcularTotalImpuestosAdicionales();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: documentoState.moneda === 'USD' ? 'USD' : 'CRC',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Toggle día de generación
  const toggleDia = (dia) => {
    setRecurrenceConfig(prev => {
      const dias = prev.diasGeneracion.includes(dia)
        ? prev.diasGeneracion.filter(d => d !== dia)
        : [...prev.diasGeneracion, dia].sort((a, b) => a - b);
      return { ...prev, diasGeneracion: dias.length > 0 ? dias : [12] };
    });
  };

  // Guardar plantilla o documento
  const handleGuardar = async () => {
    if (!receptor) {
      toast.error('Debe asignar un cliente');
      return;
    }
    if (lineas.length === 0) {
      toast.error('Agregue al menos un producto/servicio');
      return;
    }
    if (lineas.some(l => !l.detalle || l.precioUnitario <= 0)) {
      toast.error('Complete todas las líneas de detalle');
      return;
    }

    // Only validate recurrence config if not in documento mode
    if (!isDocumentoMode) {
      if (!recurrenceConfig.nombre?.trim()) {
        toast.error('El nombre de la plantilla es requerido');
        return;
      }
      if (recurrenceConfig.diasGeneracion.length === 0) {
        toast.error('Seleccione al menos un día de generación');
        return;
      }
    }

    setLoading(true);
    try {
      if (isDocumentoMode) {
        // Update existing document
        const docData = {
          notas: documentoState.comentarios,
          lineas: lineas.map((l, idx) => {
            const lineaData = {
              numeroLinea: idx + 1,
              codigo: l.codigo || null,
              codigoComercial: l.sku || null,
              codigoTipo: '04',
              detalle: l.detalle,
              cantidad: Number(l.cantidad) || 1,
              unidadMedida: l.unidadMedida || 'Unid',
              precioUnitario: Number(l.precioUnitario) || 0,
              descuentoPorcentaje: l.descuento || 0,
              impuestoCodigo: '01',
              impuestoCodigoTarifa: l.codigoTarifa || '08',
              impuestoTarifa: Number(l.iva) || 13,
              impuestosAdicionales: l.impuestosAdicionales || []
            };

            // Include exoneration if applicable
            if (lineaTieneExoneracion(l)) {
              const exoData = getExoneracionData(l);
              const porcentajeExoneracion = getPorcentajeExoneracion(l);
              const subtotal = (l.cantidad || 1) * (l.precioUnitario || 0);
              const porcentajeAplicar = Math.min(porcentajeExoneracion, l.iva || 13);
              const montoExonerado = subtotal * (porcentajeAplicar / 100);

              lineaData.impuestoExoneracion = {
                tipoDocumento: exoData.tipoDocumento,
                numeroDocumento: exoData.numeroDocumento,
                nombreInstitucion: exoData.nombreInstitucion,
                fechaEmision: exoData.fechaEmision,
                porcentaje: porcentajeAplicar,
                monto: montoExonerado
              };
            }

            return lineaData;
          })
        };
        await documentsApi.update(documento.id, docData);
        toast.success('Documento actualizado exitosamente');
      } else {
        // Template mode
        const data = {
          receptorId: receptor.id,
          nombre: recurrenceConfig.nombre,
          frecuencia: recurrenceConfig.frecuencia,
          diasGeneracion: recurrenceConfig.diasGeneracion,
          proximaGeneracion: recurrenceConfig.proximaGeneracion,
          tipoDocumento: '01',
          condicionVenta: documentoState.condicionVenta,
          medioPago: [documentoState.medioPago],
          codigoMoneda: documentoState.moneda,
          notas: documentoState.comentarios,
          referenciaOC: documentoState.referenciaOC || null,
          periodoCobroEnabled: recurrenceConfig.periodoCobroEnabled,
          periodoCobroLabel: recurrenceConfig.periodoCobroLabel,
          periodoCobroOffset: recurrenceConfig.periodoCobroOffset,
          lineas: lineas.map((l, idx) => {
            const lineaData = {
              numeroLinea: idx + 1,
              codigo: l.codigo || null,
              codigoComercial: l.sku || null,
              sku: l.sku || null,
              codigoTipo: '04',
              detalle: l.detalle,
              cantidad: Number(l.cantidad) || 1,
              unidadMedida: l.unidadMedida || 'Unid',
              precioUnitario: Number(l.precioUnitario) || 0,
              descuentoPorcentaje: l.descuento || 0,
              impuestoCodigo: '01',
              impuestoCodigoTarifa: l.codigoTarifa || '08',
              impuestoTarifa: Number(l.iva) || 13,
              impuestosAdicionales: l.impuestosAdicionales || []
            };

            // Include exoneration if applicable
            if (lineaTieneExoneracion(l)) {
              const exoData = getExoneracionData(l);
              const porcentajeExoneracion = getPorcentajeExoneracion(l);
              const subtotal = (l.cantidad || 1) * (l.precioUnitario || 0);
              const porcentajeAplicar = Math.min(porcentajeExoneracion, l.iva || 13);
              const montoExonerado = subtotal * (porcentajeAplicar / 100);

              lineaData.impuestoExoneracion = {
                tipoDocumento: exoData.tipoDocumento,
                numeroDocumento: exoData.numeroDocumento,
                nombreInstitucion: exoData.nombreInstitucion,
                fechaEmision: exoData.fechaEmision,
                porcentaje: porcentajeAplicar,
                monto: montoExonerado
              };

              // Also include exoneration data for legacy support
              lineaData.exoneracion = lineaData.impuestoExoneracion;
            }

            return lineaData;
          })
        };

        if (plantilla?.id) {
          // Update existing template
          await recurringApi.update(plantilla.id, data);
          toast.success('Plantilla actualizada exitosamente');
        } else {
          // Create new template
          await recurringApi.create(data);
          toast.success('Plantilla recurrente creada exitosamente');
        }
      }
      onSave();
    } catch (error) {
      console.error('Error guardando:', error);
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  // Preparar datos para InvoiceTemplate
  const receptorForTemplate = receptor ? {
    nombre: receptor.nombre,
    identificacion: receptor.identificacion,
    tipo_identificacion: receptor.tipoIdentificacion || receptor.tipo_identificacion || '02',
    email: receptor.email,
    email_facturacion: receptor.emailFacturacion || receptor.email_facturacion || receptor.email,
    telefono_codigo: receptor.telefonoCodigo || receptor.telefono_codigo || '506',
    telefono_numero: receptor.telefonoNumero || receptor.telefono_numero,
    provincia: receptor.provincia,
    canton: receptor.canton,
    distrito: receptor.distrito,
    otras_senas: receptor.otrasSenas || receptor.otras_senas,
    dias_credito: receptor.diasCredito || receptor.dias_credito || 30
  } : null;

  const emisorForTemplate = emisorData ? {
    nombre: emisorData.nombre,
    nombre_comercial: emisorData.nombre_comercial || emisorData.nombreComercial,
    identificacion: emisorData.identificacion,
    email: emisorData.email,
    telefono_numero: emisorData.telefono_numero || emisorData.telefonoNumero,
    logo_url: emisorData.logo_url || emisorData.logoUrl,
    telefono_codigo: emisorData.telefono_codigo || emisorData.telefonoCodigo || '506',
    provincia: emisorData.provincia,
    canton: emisorData.canton,
    distrito: emisorData.distrito,
    otras_senas: emisorData.otras_senas || emisorData.otrasSenas,
    template_color: emisorData.template_color || emisorData.templateColor || '#F47920',
    terminos_condiciones: emisorData.terminos_condiciones || emisorData.terminosCondiciones || ''
  } : null;

  const documentoForTemplate = {
    consecutivo: isDocumentoMode ? (documento?.consecutivo || 'BORRADOR') : 'PLANTILLA',
    tipoDocumento: '01',
    fechaEmision: new Date().toISOString(),
    condicionVenta: documentoState.condicionVenta,
    medioPago: [documentoState.medioPago],
    codigoMoneda: documentoState.moneda,
    status: 'draft',
    notas: documentoState.comentarios,
    ordenCompra: documentoState.referenciaOC || null,
    diasCredito: documentoState.diasCredito,
    periodoCobroTexto: isDocumentoMode
      ? (documento?.periodoCobroTexto || documento?.periodo_cobro_texto)
      : (recurrenceConfig.periodoCobroEnabled ? (() => {
          const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
          const d = recurrenceConfig.proximaGeneracion ? new Date(recurrenceConfig.proximaGeneracion + 'T12:00:00') : new Date();
          const off = recurrenceConfig.periodoCobroOffset || 0;
          if (recurrenceConfig.frecuencia === 'yearly') d.setFullYear(d.getFullYear() + off);
          else d.setMonth(d.getMonth() + off);
          let periodo;
          if (recurrenceConfig.frecuencia === 'yearly') periodo = `Año ${d.getFullYear()}`;
          else if (recurrenceConfig.frecuencia === 'quarterly') {
            const m = d.getMonth();
            const finM = (m + 2) % 12;
            const finA = m + 2 >= 12 ? d.getFullYear() + 1 : d.getFullYear();
            periodo = `${MESES[m]} - ${MESES[finM]} ${finA}`;
          } else periodo = `${MESES[d.getMonth()]} ${d.getFullYear()}`;
          return `${recurrenceConfig.periodoCobroLabel}: ${periodo}`;
        })() : null)
  };

  const lineasForTemplate = lineas.map((l, idx) => {
    const cantidad = Number(l.cantidad) || 1;
    const precio = Number(l.precioUnitario) || 0;
    const tarifa = Number(l.iva) || 0;
    const montoTotal = cantidad * precio;
    const impuestoMonto = montoTotal * (tarifa / 100);

    // Calculate exoneration if applicable
    let impuestoNeto = impuestoMonto;
    let impuestoExoneracion = null;
    if (lineaTieneExoneracion(l)) {
      const porcentajeExoneracion = getPorcentajeExoneracion(l);
      const porcentajeAplicar = Math.min(porcentajeExoneracion, tarifa);
      const montoExonerado = montoTotal * (porcentajeAplicar / 100);
      impuestoNeto = impuestoMonto - montoExonerado;
      if (impuestoNeto < 0) impuestoNeto = 0;

      const exoData = getExoneracionData(l);
      impuestoExoneracion = {
        tipoDocumento: exoData.tipoDocumento,
        tipoDocumentoDesc: exoData.tipoDocumentoDesc || 'Exoneración',
        numeroDocumento: exoData.numeroDocumento,
        nombreInstitucion: exoData.nombreInstitucion,
        fechaEmision: exoData.fechaEmision,
        porcentaje: porcentajeAplicar,
        monto: montoExonerado
      };
    }

    // Calculate additional taxes (911, Cruz Roja)
    const impuestosAdicionales = (l.impuestosAdicionales || []).map(imp => ({
      ...imp,
      monto: montoTotal * (imp.porcentaje / 100)
    }));
    const totalImpuestosAdicionales = impuestosAdicionales.reduce((sum, imp) => sum + imp.monto, 0);

    return {
      numeroLinea: idx + 1,
      codigo: l.codigo || '',
      sku: l.sku || '',
      codigoComercial: l.sku || '',
      detalle: l.detalle || '',
      cantidad: cantidad,
      unidadMedida: l.unidadMedida || 'Unid',
      precioUnitario: precio,
      montoTotal: montoTotal,
      descuentoMonto: 0,
      subtotal: montoTotal,
      impuestoTarifa: tarifa,
      impuestoMonto: impuestoMonto,
      impuestoNeto: impuestoNeto,
      impuestoExoneracion: impuestoExoneracion,
      impuestosAdicionales: impuestosAdicionales,
      montoTotalLinea: montoTotal + impuestoNeto + totalImpuestosAdicionales
    };
  });

  // Build impuestosDesglose for the template (IVA + additional taxes)
  const buildImpuestosDesglose = () => {
    const desglose = [];

    // Add IVA
    const ivaTotal = calcularIVA();
    if (ivaTotal > 0) {
      desglose.push({
        codigo: '01',
        descripcion: 'IVA',
        monto: ivaTotal
      });
    }

    // Add additional taxes (911, Cruz Roja)
    const impuestosAdicionales = calcularImpuestosAdicionales();
    impuestosAdicionales.forEach(imp => {
      desglose.push({
        codigo: imp.codigoImpuesto,
        descripcion: imp.descripcion,
        monto: imp.monto
      });
    });

    return desglose;
  };

  // Exonerado = Exento (sin IVA)
  const calcularTotalesGravadoExento = () => {
    let gravado = 0;
    let exento = 0;
    lineas.forEach(l => {
      const subtotal = (l.cantidad || 0) * (l.precioUnitario || 0);
      if (lineaTieneExoneracion(l) || (l.iva || 0) === 0) {
        exento += subtotal;
      } else {
        gravado += subtotal;
      }
    });
    return { gravado, exento };
  };

  const totalesGravadoExento = calcularTotalesGravadoExento();

  const totalesForTemplate = {
    totalGravado: totalesGravadoExento.gravado,
    totalExento: totalesGravadoExento.exento,
    totalDescuentos: 0,
    totalImpuesto: calcularIVA() + calcularTotalImpuestosAdicionales(),
    impuestosDesglose: buildImpuestosDesglose(),
    totalVenta: calcularSubtotal(),
    totalVentaNeta: calcularSubtotal(),
    totalOtrosCargos: 0,
    totalComprobante: calcularTotal()
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden print:overflow-visible print:static">
      <div className="flex h-full print:block">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 print:hidden" onClick={onClose} />

        <div className="relative flex w-full h-full print:block">
          {/* Columna Izquierda - InvoiceTemplate Preview con slide animation */}
          <div
            className={clsx(
              "h-full overflow-y-auto print:w-full print:block print:bg-white bg-gray-100 transition-all duration-300 ease-in-out flex-shrink-0",
              previewMode === 'full' ? "w-full opacity-100" : previewMode === 'split' ? "w-2/5 opacity-100" : "w-0 opacity-0 overflow-hidden"
            )}
          >
            <div className="bg-white h-full w-full p-4 min-w-[350px]">
              {/* Header de plantilla/documento */}
              <div className={clsx(
                "mb-4 p-3 rounded-lg border",
                isDocumentoMode ? "bg-purple-50/60 border-purple-200/70" : "bg-cyan-50/60 border-cyan-200/70"
              )}>
                <div className={clsx(
                  "flex items-center gap-2",
                  isDocumentoMode ? "text-purple-600" : "text-cyan-600"
                )}>
                  {isDocumentoMode ? <FileText className="w-5 h-5" /> : <Repeat className="w-5 h-5" />}
                  <span className="font-semibold">
                    {isDocumentoMode ? 'Vista Previa del Documento' : 'Vista Previa de Plantilla Recurrente'}
                  </span>
                </div>
              </div>

              <InvoiceTemplate
                documento={documentoForTemplate}
                emisor={emisorForTemplate}
                receptor={receptorForTemplate}
                lineas={lineasForTemplate}
                totales={totalesForTemplate}
                esCotizacion={false}
                tipoCambio={documentoState.tipoCambio || 1}
                fullWidth={true}
              />
            </div>
          </div>

          {/* Controles de vista previa: ◀ reduce (más formulario) / ▶ agranda (PDF completo) */}
          <div
            className="absolute top-1/2 -translate-y-1/2 z-20 flex flex-col bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden print:hidden"
            style={{ left: previewMode === 'full' ? 'calc(100% - 30px)' : previewMode === 'split' ? 'calc(40% - 1px)' : '0px', transition: 'left 0.3s ease-in-out' }}
          >
            <button
              onClick={() => setPreviewMode(previewMode === 'full' ? 'split' : 'hidden')}
              disabled={previewMode === 'hidden'}
              className="px-1 py-2.5 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default"
              title="Menos vista previa (más formulario)"
            >
              <ChevronLeft size={16} className="text-gray-600" />
            </button>
            <button
              onClick={() => setPreviewMode(previewMode === 'hidden' ? 'split' : 'full')}
              disabled={previewMode === 'full'}
              className="px-1 py-2.5 border-t border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default"
              title="Más vista previa (ver todo el PDF)"
            >
              <ChevronRight size={16} className="text-gray-600" />
            </button>
          </div>

          {/* Columna Derecha - Formulario */}
          <div className="flex-1 min-w-0 h-full bg-white flex flex-col border-l border-gray-200 print:hidden transition-all duration-300 ease-in-out overflow-hidden">
            {/* Header */}
            <div className={clsx(
              "flex items-center justify-between px-4 py-3 border-b border-gray-200",
              isDocumentoMode ? "bg-purple-50/70" : "bg-cyan-50/70"
            )}>
              <div className="flex items-center gap-3">
                <div className={clsx(
                  "p-2 rounded-lg",
                  isDocumentoMode ? "bg-purple-100/80" : "bg-cyan-100/80"
                )}>
                  {isDocumentoMode ? (
                    <FileText className="w-6 h-6 text-purple-600" />
                  ) : (
                    <Repeat className="w-6 h-6 text-cyan-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {isDocumentoMode
                      ? 'Editar Documento'
                      : (plantilla ? 'Editar Plantilla Recurrente' : 'Nueva Plantilla Recurrente')}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {isDocumentoMode
                      ? 'Modifique el documento antes de enviarlo a Hacienda'
                      : (plantilla ? 'Modifique la factura y configuración de recurrencia' : 'Configure la factura que se generará automáticamente')}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowClientModal(true)}
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border',
                  receptor ? 'border-green-400 text-green-600 bg-green-50' : 'border-cyan-400 text-cyan-600 hover:bg-cyan-50'
                )}
              >
                <User className="w-4 h-4" />
                {receptor ? 'Cambiar Cliente' : 'Asignar Cliente'}
              </button>

              <div className="h-6 w-px bg-gray-300" />

              <button
                onClick={() => eliminarLinea()}
                disabled={selectedLineIds.length === 0}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Delete className="h-5 w-5 text-red-500" />
                Eliminar ({selectedLineIds.length})
              </button>

              <div className="h-6 w-px bg-gray-300" />

              <button
                onClick={() => setShowCommentsModal(true)}
                className={clsx(
                  'flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border',
                  documentoState.comentarios ? 'border-blue-400 text-blue-600 bg-blue-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                )}
              >
                <MessageSquare className="w-4 h-4" />
                Comentarios
              </button>

              <button
                onClick={() => setShowOpcionesModal(true)}
                className={clsx(
                  'flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border',
                  documentoState.referenciaOC ? 'border-amber-400 text-amber-600 bg-amber-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                )}
              >
                <Settings className="w-4 h-4" />
                Opciones
              </button>

              <div className="flex-1" />

              <select
                value={documentoState.moneda}
                onChange={(e) => setDocumentoState(prev => ({
                  ...prev,
                  moneda: e.target.value,
                  tipoCambio: e.target.value === 'USD' ? (storeTipoCambio || 1) : 1
                }))}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="USD">USD ($)</option>
                <option value="CRC">CRC (₡)</option>
              </select>

              <select
                value={documentoState.condicionVenta}
                onChange={(e) => setDocumentoState(prev => ({ ...prev, condicionVenta: e.target.value }))}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {CONDICIONES_VENTA.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Loading state when loading template */}
              {loadingTemplate ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-600 mx-auto mb-3" />
                    <p className="text-gray-500">Cargando plantilla...</p>
                  </div>
                </div>
              ) : (
              <>
              {/* Cliente Info */}
              {receptor && (
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{receptor.nombre}</p>
                        <p className="text-sm text-gray-600">{receptor.identificacion}</p>
                        {receptor.email && <p className="text-sm text-gray-500">{receptor.email}</p>}
                      </div>
                      {(() => {
                        // Verificar si hay exoneración en las líneas o en el receptor
                        const lineaConExo = lineas.find(l => l.impuestoExoneracion);
                        const porcentaje = lineaConExo?.impuestoExoneracion?.porcentaje || getPorcentajeExoneracion();
                        const tieneExoneracion = lineaConExo || exoneracionTemporal || receptor?.exoneracionActiva || receptor?.exoneracion_activa;
                        if (!tieneExoneracion || porcentaje <= 0) return null;
                        return (
                          <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                            <Shield className="w-3 h-3" />
                            EXONERADO {porcentaje}%
                          </div>
                        );
                      })()}
                    </div>

                    {/* Exoneración temporal - mostrar como en la ficha del cliente */}
                    {exoneracionTemporal && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <div className="bg-purple-50 border-l-4 border-purple-400 rounded-r-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-sm text-purple-900">{exoneracionTemporal.numeroDocumento || '-'}</span>
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">Activa</span>
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded">{exoneracionTemporal.porcentaje}%</span>
                            </div>
                            <button onClick={quitarExoneracion} className="text-red-500 hover:text-red-700 text-xs font-medium hover:underline">Quitar</button>
                          </div>
                          <div className="text-xs text-gray-600 space-y-0.5">
                            {exoneracionTemporal.nombreInstitucion && (
                              <p>{exoneracionTemporal.nombreInstitucion}</p>
                            )}
                            <p>
                              {exoneracionTemporal.fechaEmision && <span>Emisión: {formatearFecha(exoneracionTemporal.fechaEmision)}</span>}
                              {exoneracionTemporal.fechaVencimiento && <span className="ml-3">Vence: {formatearFecha(exoneracionTemporal.fechaVencimiento)}</span>}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Botón para agregar exoneración si no tiene */}
                    {(() => {
                      const tieneExoneracion = exoneracionTemporal || receptor?.exoneracionActiva || receptor?.exoneracion_activa;
                      if (tieneExoneracion) return null;
                      return (
                        <div className="mt-2 pt-2 border-t border-green-200">
                          <button
                            onClick={() => setShowExoneracionRapidaModal(true)}
                            className="w-full text-xs px-2 py-1.5 border border-purple-300 text-purple-600 rounded hover:bg-purple-50 flex items-center justify-center gap-1 transition-colors"
                          >
                            <Shield className="h-3 w-3" />
                            Agregar Exoneración
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Búsqueda de productos */}
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    ref={productInputRef}
                    type="text"
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    onKeyDown={handleProductSearch}
                    onFocus={() => productSearchResults.length > 0 && setShowProductDropdown(true)}
                    placeholder="Buscar producto o escribir descripción + Enter..."
                    className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-0 outline-none"
                  />
                  {searchingProducts && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
                  )}

                  {/* Dropdown de productos */}
                  {showProductDropdown && productSearchResults.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {productSearchResults.map((producto, index) => (
                        <button
                          key={producto.id}
                          onClick={() => seleccionarProducto(producto)}
                          className={clsx(
                            'w-full px-4 py-2 text-left border-b border-gray-100 last:border-0 transition-colors',
                            index === highlightedIndex ? 'bg-cyan-50 border-l-4 border-l-cyan-500' : 'hover:bg-gray-50'
                          )}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="text-xs text-gray-500 font-mono">{producto.codigo}</div>
                              <div className="font-medium text-gray-900">{producto.descripcion || producto.nombre}</div>
                              <div className="text-xs text-gray-500 flex gap-2 mt-1">
                                {producto.codigoCabys && (
                                  <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-mono">
                                    {producto.codigoCabys}
                                  </span>
                                )}
                                <span>IVA: {producto.tarifaIva}%</span>
                                {producto.impuestos?.some(i => i.codigoImpuesto === '07') && (
                                  <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">911</span>
                                )}
                                {producto.impuestos?.some(i => i.codigoImpuesto === '08') && (
                                  <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Cruz Roja</span>
                                )}
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              {producto.moneda === 'USD' ? '$' : '₡'}{producto.precio?.toLocaleString('es-CR')}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Click outside to close */}
              {showProductDropdown && (
                <div className="fixed inset-0 z-40" onClick={() => { setShowProductDropdown(false); setSelectedProducts([]); }} />
              )}

              {/* Tabla de líneas */}
              <div className="flex-1 px-4 py-3 overflow-x-auto">
                <div className="border border-gray-200 rounded-lg overflow-hidden min-w-[900px]">
                  {/* Header */}
                  <div className="bg-gray-100 border-b border-gray-200">
                    <div className="flex items-center py-2 px-2 text-xs font-semibold text-gray-600 uppercase">
                      <div className="w-6"></div>
                      <div className="w-8 flex justify-center">
                        <input
                          type="checkbox"
                          checked={lineas.length > 0 && selectedLineIds.length === lineas.length}
                          onChange={toggleSelectAllLines}
                          className="rounded border-gray-400"
                        />
                      </div>
                      <div className="w-36 px-2">CABYS</div>
                      <div className="w-24 px-2">Código</div>
                      <div className="flex-1 px-2 min-w-[150px] max-w-[400px]">Descripción</div>
                      <div className="w-20 text-right px-2">Cantidad</div>
                      <div className="w-28 text-right px-2">Precio</div>
                      <div className="w-16 text-right px-2">Desc.</div>
                      <div className="w-16 text-center px-2">IVA</div>
                      <div className="w-24 text-right px-2">Impuesto</div>
                      <div className="w-28 text-right px-2">Total</div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                    {lineas.length === 0 ? (
                      <div className="py-12 text-center text-gray-400">
                        <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No hay productos agregados</p>
                        <p className="text-sm">Use la barra de búsqueda para agregar</p>
                      </div>
                    ) : (
                      lineas.map((linea) => {
                        const subtotal = linea.cantidad * linea.precioUnitario;
                        const ivaBruto = subtotal * (linea.iva / 100);
                        const tieneExo = lineaTieneExoneracion(linea);
                        const montoExonerado = tieneExo ? subtotal * (Math.min(getPorcentajeExoneracion(linea), linea.iva) / 100) : 0;
                        const ivaMonto = ivaBruto - montoExonerado; // IVA neto después de exoneración
                        const total = subtotal + ivaMonto;

                        return (
                          <div
                            key={linea.id}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, linea.id)}
                            className={clsx(
                              'flex items-center py-2 px-2 transition-colors',
                              selectedLineIds.includes(linea.id) ? 'bg-cyan-50' : 'hover:bg-gray-50',
                              draggedLineId === linea.id ? 'opacity-50' : ''
                            )}
                          >
                            <div
                              draggable
                              onDragStart={(e) => handleDragStart(e, linea.id)}
                              onDragEnd={handleDragEnd}
                              className="w-6 flex justify-center cursor-grab text-gray-400 hover:text-gray-600"
                            >
                              <GripVertical className="h-4 w-4" />
                            </div>
                            <div className="w-8 flex justify-center">
                              <input
                                type="checkbox"
                                checked={selectedLineIds.includes(linea.id)}
                                onChange={() => toggleLineSelection(linea.id)}
                                className="rounded border-gray-300"
                              />
                            </div>
                            <div className="w-36 px-2">
                              <input
                                type="text"
                                value={linea.codigo}
                                onChange={(e) => actualizarLinea(linea.id, 'codigo', e.target.value)}
                                className="w-full px-1 py-1 border border-gray-300 rounded text-xs font-mono"
                                placeholder="CABYS"
                              />
                            </div>
                            <div className="w-24 px-2">
                              <input
                                type="text"
                                value={linea.sku || ''}
                                onChange={(e) => actualizarLinea(linea.id, 'sku', e.target.value)}
                                className="w-full px-1 py-1 border border-gray-300 rounded text-xs font-mono"
                                placeholder="Código"
                              />
                            </div>
                            <div className="flex-1 px-2 min-w-[150px] max-w-[400px]">
                              <textarea
                                value={linea.detalle}
                                onChange={(e) => actualizarLinea(linea.id, 'detalle', e.target.value)}
                                className="w-full px-1.5 py-1 border border-gray-300 rounded text-sm resize-none"
                                placeholder="Descripción"
                                rows={Math.max(1, (linea.detalle || '').split('\n').length)}
                                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                              />
                              {tieneExo && getPorcentajeExoneracion(linea) > 0 && (
                                <div className="mt-1 text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                  Exonerado {getPorcentajeExoneracion(linea)}%
                                </div>
                              )}
                              {linea.impuestosAdicionales?.length > 0 && (
                                <div className="mt-1 flex gap-1">
                                  {linea.impuestosAdicionales.map((imp, idx) => (
                                    <span key={idx} className={clsx(
                                      'text-[10px] px-1.5 py-0.5 rounded',
                                      imp.codigoImpuesto === '07' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                                    )}>
                                      {imp.codigoImpuesto === '07' ? '911' : 'Cruz Roja'} {imp.porcentaje}%
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="w-20 px-2">
                              <input
                                type="number"
                                value={linea.cantidad}
                                onChange={(e) => actualizarLinea(linea.id, 'cantidad', parseFloat(e.target.value) || 1)}
                                className="w-full px-1 py-1 border border-gray-300 rounded text-sm text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                min="0.001"
                                step="0.001"
                              />
                            </div>
                            <div className="w-28 px-2">
                              <input
                                type="number"
                                data-line-price={linea.id}
                                value={linea.precioUnitario}
                                onChange={(e) => actualizarLinea(linea.id, 'precioUnitario', parseFloat(e.target.value) || 0)}
                                className="w-full px-1 py-1 border border-gray-300 rounded text-sm text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <div className="w-16 px-2">
                              <div className="flex items-center gap-0.5">
                                <input
                                  type="number"
                                  value={linea.descuento || 0}
                                  onChange={(e) => actualizarLinea(linea.id, 'descuento', parseFloat(e.target.value) || 0)}
                                  className="w-10 px-1 py-1 border border-gray-300 rounded text-sm text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-xs text-gray-400">%</span>
                              </div>
                            </div>
                            <div className="w-16 px-2">
                              <select
                                value={linea.iva}
                                onChange={(e) => actualizarLinea(linea.id, 'iva', parseFloat(e.target.value))}
                                className="w-full px-1 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="0">0%</option>
                                <option value="1">1%</option>
                                <option value="2">2%</option>
                                <option value="4">4%</option>
                                <option value="8">8%</option>
                                <option value="13">13%</option>
                              </select>
                            </div>
                            <div className="w-24 px-2 text-right">
                              <span className="text-sm text-gray-600">{formatCurrency(ivaMonto)}</span>
                            </div>
                            <div className="w-28 px-2 text-right">
                              <div className="font-semibold text-sm">{formatCurrency(total)}</div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Totales */}
                {lineas.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between text-gray-600">
                        <span>Subtotal:</span>
                        <span className="text-sm">{formatCurrency(calcularSubtotal())}</span>
                      </div>
                      {calcularImpuestosAdicionales().map((imp, idx) => (
                        <div key={idx} className="flex justify-between text-gray-600">
                          <span>{imp.descripcion} ({imp.porcentaje}%):</span>
                          <span className="text-sm">{formatCurrency(imp.monto)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-gray-600">
                        <span>IVA:</span>
                        <span className="text-sm">{formatCurrency(calcularIVA())}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2 mt-2">
                        <span>Total:</span>
                        <span>{formatCurrency(calcularTotal())}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Configuración de Recurrencia - Solo para plantillas, no documentos */}
                {!isDocumentoMode && (
                  <div className="mt-4 p-4 bg-cyan-50/60 border border-cyan-200/70 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="w-5 h-5 text-cyan-600" />
                      <h3 className="font-semibold text-cyan-700">Configuración de Recurrencia</h3>
                    </div>

                    <div className="space-y-4">
                      {/* Nombre */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre de la plantilla *
                        </label>
                        <input
                          type="text"
                          value={recurrenceConfig.nombre}
                          onChange={(e) => setRecurrenceConfig(prev => ({ ...prev, nombre: e.target.value }))}
                          placeholder="Ej: Mantenimiento mensual - Cliente ABC"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      </div>

                      {/* Frecuencia y Próxima generación */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia</label>
                          <select
                            value={recurrenceConfig.frecuencia}
                            onChange={(e) => setRecurrenceConfig(prev => ({ ...prev, frecuencia: e.target.value, periodoCobroOffset: 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          >
                            <option value="weekly">Semanal</option>
                            <option value="biweekly">Quincenal</option>
                            <option value="monthly">Mensual</option>
                            <option value="quarterly">Trimestral</option>
                            <option value="yearly">Anual</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Próxima generación</label>
                          <input
                            type="date"
                            value={recurrenceConfig.proximaGeneracion}
                            onChange={(e) => setRecurrenceConfig(prev => ({ ...prev, proximaGeneracion: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>

                      {/* Días de generación */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Días de generación (del mes)
                        </label>
                        <div className="grid grid-cols-10 gap-1">
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(dia => (
                            <button
                              key={dia}
                              type="button"
                              onClick={() => toggleDia(dia)}
                              className={clsx(
                                'w-8 h-8 rounded text-xs font-medium transition-colors',
                                recurrenceConfig.diasGeneracion.includes(dia)
                                  ? 'bg-cyan-500 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              )}
                            >
                              {dia}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Seleccionados: {recurrenceConfig.diasGeneracion.join(', ')}
                        </p>
                      </div>

                      {/* Periodo de Cobro */}
                      <div className="border-t border-gray-200 pt-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer mb-3">
                          <input
                            type="checkbox"
                            checked={recurrenceConfig.periodoCobroEnabled}
                            onChange={(e) => setRecurrenceConfig(prev => ({ ...prev, periodoCobroEnabled: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-sm font-medium text-gray-700">Incluir periodo de cobro en la factura</span>
                        </label>
                        {recurrenceConfig.periodoCobroEnabled && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Etiqueta</label>
                                <input
                                  type="text"
                                  value={recurrenceConfig.periodoCobroLabel}
                                  onChange={(e) => setRecurrenceConfig(prev => ({ ...prev, periodoCobroLabel: e.target.value }))}
                                  placeholder="Periodo de Cobro"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Periodo a cobrar</label>
                                <select
                                  value={recurrenceConfig.periodoCobroOffset}
                                  onChange={(e) => setRecurrenceConfig(prev => ({ ...prev, periodoCobroOffset: parseInt(e.target.value) }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                >
                                  {recurrenceConfig.frecuencia === 'yearly' ? (
                                    <>
                                      <option value={0}>Año actual</option>
                                      <option value={-1}>Año anterior (servicio vencido)</option>
                                      <option value={-2}>2 años atrás</option>
                                      <option value={1}>Año siguiente (anticipo)</option>
                                    </>
                                  ) : recurrenceConfig.frecuencia === 'quarterly' ? (
                                    <>
                                      <option value={0}>Trimestre actual</option>
                                      <option value={-3}>Trimestre anterior (servicio vencido)</option>
                                      <option value={3}>Trimestre siguiente (anticipo)</option>
                                    </>
                                  ) : (
                                    <>
                                      <option value={0}>Mes actual</option>
                                      <option value={-1}>Mes anterior (servicio vencido)</option>
                                      <option value={-2}>2 meses atrás</option>
                                      <option value={1}>Mes siguiente (anticipo)</option>
                                    </>
                                  )}
                                </select>
                              </div>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                              <span className="text-sm font-semibold text-blue-700">{(() => {
                                const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
                                const d = recurrenceConfig.proximaGeneracion ? new Date(recurrenceConfig.proximaGeneracion + 'T12:00:00') : new Date();
                                const offset = recurrenceConfig.periodoCobroOffset || 0;
                                // Para anual, offset en años; para resto en meses (alineado con backend).
                                if (recurrenceConfig.frecuencia === 'yearly') {
                                  d.setFullYear(d.getFullYear() + offset);
                                } else {
                                  d.setMonth(d.getMonth() + offset);
                                }
                                let periodo;
                                if (recurrenceConfig.frecuencia === 'yearly') {
                                  periodo = `Año ${d.getFullYear()}`;
                                } else if (recurrenceConfig.frecuencia === 'quarterly') {
                                  const m = d.getMonth();
                                  const finM = (m + 2) % 12;
                                  const finA = m + 2 >= 12 ? d.getFullYear() + 1 : d.getFullYear();
                                  periodo = `${MESES[m]} - ${MESES[finM]} ${finA}`;
                                } else {
                                  periodo = `${MESES[d.getMonth()]} ${d.getFullYear()}`;
                                }
                                return `${recurrenceConfig.periodoCobroLabel}: ${periodo}`;
                              })()}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardar}
                disabled={loading || loadingTemplate || !receptor || lineas.length === 0}
                className={clsx(
                  "flex items-center gap-2 px-6 py-2 border-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed",
                  isDocumentoMode
                    ? "border-purple-500 text-purple-600 hover:bg-purple-50"
                    : "border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                )}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isDocumentoMode
                  ? 'Guardar Documento'
                  : (plantilla ? 'Guardar Cambios' : 'Crear Plantilla Recurrente')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de búsqueda de cliente */}
      {showClientModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setShowClientModal(false)} />
            <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Buscar Cliente</h3>
                <button onClick={() => setShowClientModal(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, cédula o correo..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  autoFocus
                />
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {searchingClient ? (
                  <div className="p-4 text-center text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Buscando...
                  </div>
                ) : clientSearchResults.length > 0 ? (
                  clientSearchResults.map(cliente => (
                    <button
                      key={cliente.id}
                      onClick={() => seleccionarCliente(cliente)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <p className="font-medium">{cliente.nombre}</p>
                      <p className="text-sm text-gray-500">{cliente.identificacion}</p>
                    </button>
                  ))
                ) : clientSearchTerm.length >= 2 ? (
                  <div className="p-4 text-center text-gray-500">
                    No se encontraron clientes
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-400">
                    Escriba al menos 2 caracteres para buscar
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => { setShowClientModal(false); setShowNuevoClienteModal(true); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-cyan-400 text-cyan-600 rounded-lg hover:bg-cyan-50"
                >
                  <Plus className="w-4 h-4" />
                  Crear Nuevo Cliente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de nuevo cliente */}
      {showNuevoClienteModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setShowNuevoClienteModal(false)} />
            <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
              <FormularioCliente
                onClose={() => setShowNuevoClienteModal(false)}
                onSave={(nuevoCliente) => {
                  setShowNuevoClienteModal(false);
                  seleccionarCliente(nuevoCliente);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Exoneración */}
      <ExoneracionModal
        isOpen={showExoneracionRapidaModal}
        onClose={() => setShowExoneracionRapidaModal(false)}
        onExoneracionSelected={setExoneracionTemporal}
        clienteIdentificacion={receptor?.identificacion}
        clienteNombre={receptor?.nombre}
        subtitulo="Solo para esta plantilla"
      />
      {/* Modal Comentarios */}
      {showCommentsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Comentarios / Notas</h2>
              <button onClick={() => setShowCommentsModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <textarea
                value={documentoState.comentarios}
                onChange={(e) => setDocumentoState(prev => ({ ...prev, comentarios: e.target.value }))}
                placeholder="Agregar notas o comentarios a la factura..."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                autoFocus
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setDocumentoState(prev => ({ ...prev, comentarios: '' }))}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Limpiar
              </button>
              <button
                onClick={() => setShowCommentsModal(false)}
                className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Opciones de Factura */}
      {showOpcionesModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Opciones de Factura</h2>
              <button onClick={() => setShowOpcionesModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Términos de Pago</label>
                <select
                  value={documentoState.condicionVenta}
                  onChange={(e) => setDocumentoState(prev => ({ ...prev, condicionVenta: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {CONDICIONES_VENTA.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                <select
                  value={documentoState.moneda}
                  onChange={(e) => setDocumentoState(prev => ({
                    ...prev,
                    moneda: e.target.value,
                    tipoCambio: e.target.value === 'USD' ? (storeTipoCambio || 1) : 1
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="USD">$ Dólares (USD)</option>
                  <option value="CRC">₡ Colones (CRC)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cambio</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₡</span>
                  <input
                    type="number"
                    step="0.01"
                    value={documentoState.tipoCambio}
                    onChange={(e) => setDocumentoState(prev => ({ ...prev, tipoCambio: parseFloat(e.target.value) || 1 }))}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">TC del BCCR: ₡{storeTipoCambio || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referencia / Orden de Compra</label>
                <input
                  type="text"
                  value={documentoState.referenciaOC || ''}
                  onChange={(e) => setDocumentoState(prev => ({ ...prev, referenciaOC: e.target.value }))}
                  placeholder="Ej: PO-2024-001234"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Se incluirá en las notas de la factura</p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowOpcionesModal(false)}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
